import { Fragment, FC, memo, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Spinner } from 'theme-ui';
import { Scene } from 'types/Scene';
import { useDispatch, useSelector } from 'react-redux';
import { getRatioChanged, isSeeking, setRatioChanged, setSeeking } from 'modules/canvas';
import { getCurrentScene, getSceneIdByTimeline, getUsedPaidFeatures } from 'modules/scenes';
import { getEffects, updateScenesEffectsFont } from 'modules/effects';
import { loadFont } from 'utils/textTool';
import { getActiveTool } from 'modules/activeTool';
import { KonvaContainer } from './preview/KonvaContainer';
import { SceneVolume } from 'types/sceneVolume';
import { Audio } from 'types/Audio';
import { isNil } from 'ramda';
import { getReplaceState, setReplaceAvailable } from 'modules/replaceSceneContent';
import { isSubscribed } from 'modules/user';
import { getScale, getScaleChangeFit } from 'utils/konva';
import useAsync from 'react-use/lib/useAsync';
import { getRatioFormat } from 'modules/ratioFormat';
import { getSceneSources } from 'modules/scenesLoader';
import { saveRevision } from 'modules/revisions';
import { ToolName } from 'types/AppState';
import { usePlayer } from 'utils/PlayerContext';
import { PlayerCanvas } from './preview/PlayerCanvas';
import { Player } from 'utils/player/Player';

export type PreviewProps = {
  playing: boolean;
  scenes: Scene[];
  sceneVolume: SceneVolume;
  audio: Audio;
  handleCancelReplace: () => void;
  visible: boolean;
  width: number;
  height: number;
};

export const Preview: FC<PreviewProps> = memo(
  ({ scenes, playing, sceneVolume, audio, handleCancelReplace, visible, width, height }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const player: Player = usePlayer();
    const [loadedFonts, setLoadedFonts] = useState<Array<string>>([]); // to re-draw Konva when font loaded
    const dispatch = useDispatch();
    const { isReplacing, isReplaceAvailable } = useSelector(getReplaceState);
    const currentSceneId = useSelector(getSceneIdByTimeline(player));
    const ratioFormat = useSelector(getRatioFormat);
    const ratioFormatChanged = useSelector(getRatioChanged);
    const effects = useSelector(getEffects);
    const activeTool = useSelector(getActiveTool);
    const usedPaidFeatures = useSelector(getUsedPaidFeatures);
    const isUserSubscribed = useSelector(isSubscribed);
    const waitForVideoSeeking = useSelector(isSeeking);
    const scenesSources = useSelector(getSceneSources);
    const currentScene = useSelector(getCurrentScene);

    const setReplaceAvailableHandler = useCallback(
      (val: boolean) => dispatch(setReplaceAvailable(val)),
      []
    );

    useEffect(() => {
      if (containerRef.current) {
        containerRef.current.appendChild(player.canvas);

        return () => {
          containerRef?.current?.childNodes?.forEach(item =>
            containerRef.current.removeChild(item)
          );
        };
      }
    }, [player, containerRef]);

    useEffect(() => {
      if (waitForVideoSeeking) {
        player.waitForVideoSeeking();
        dispatch(setSeeking(false));
      }
    }, [waitForVideoSeeking, player]);

    useEffect(() => {
      player.setWatermarkEnabled(!!usedPaidFeatures.length && !isUserSubscribed);
    }, [player, usedPaidFeatures, isUserSubscribed]);

    useEffect(() => {
      if (isNil(audio)) {
        player.deleteAudio();
      } else {
        player.setAudio(audio, playing);
      }
    }, [player, playing]);

    useEffect(() => {
      player.setVolume(sceneVolume);
    }, [sceneVolume, player]);

    const { loading } = useAsync(async () => {
      await player.setScenes(scenes);
      player.setTime(currentScene.start);
    }, [player, scenes, scenesSources]);

    useEffect(() => {
      player.setEffects(effects);
    }, [player, effects]);

    useEffect(() => {
      player.setSize(width, height);
    }, [player, width, height]);

    useEffect(() => {
      if (isNil(ratioFormatChanged)) return;

      const scale = getScaleChangeFit(ratioFormatChanged, ratioFormat);

      dispatch(setRatioChanged(null));
      dispatch(updateScenesEffectsFont(scale));
      dispatch(saveRevision(ToolName.Fit));
    }, [ratioFormatChanged, ratioFormat, scenes]);

    // // load all already used fonts
    useEffect(() => {
      Object.values(effects).forEach(effectsArray => {
        effectsArray.forEach(async effect => {
          await loadFont(effect.font);
        });
        setLoadedFonts(fonts => [...fonts, ...effectsArray.map(effect => effect.font.fontFamily)]);
      });
    }, [effects]);

    const sceneParams = useMemo(() => {
      const scene = scenes.find(({ id }) => id === currentSceneId);
      if (isNil(scene)) return;

      const sceneEffects = effects[scene.id];
      const scale = getScale(scene.scalingMode, width, height);
      return { scene, effects: sceneEffects, scale };
    }, [scenes, width, height, currentSceneId, effects]);

    return (
      <Fragment>
        <PlayerCanvas ref={containerRef} visible={visible} loading={loading} />
        {loading && (
          <Spinner
            size={40}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          />
        )}
        {width && height && sceneParams?.scale ? (
          <div sx={{ visibility: visible ? 'visible' : 'hidden' }}>
            <KonvaContainer
              width={width}
              height={height}
              playing={playing}
              loadedFonts={loadedFonts}
              activeTool={activeTool}
              isReplaceAvailable={isReplaceAvailable}
              isReplacing={isReplacing}
              setReplaceAvailableHandler={setReplaceAvailableHandler}
              handleCancelReplace={handleCancelReplace}
              {...sceneParams}
            />
          </div>
        ) : null}
      </Fragment>
    );
  }
);
